/**
 * A PAPÍR ÉS A TÉTELSOROK ÖSSZEKÖTÉSE (#64, 4. lépés).
 *
 * A jelölő a puska sora (a `BookingProposal`), tehát a két hasáb UGYANAZT a
 * sort mutatja két helyen: a jobb hasábban a papíron, a középsőben a
 * Tételsorok közt. Ez a horog tartja azt, ami a kettőé közösen — melyik sor
 * van kiemelve, és hová kell vinni a fókuszt —, és a kettő közül egyik sem
 * nyúl a másik belsejébe.
 *
 * A JEGYZET MEZŐJE A TÉTELSOROKBAN NYÍLIK, nem a papír mellett. A vászon
 * (`docs/tervezes/design/eredeti-jegyzet/`) külön „Jegyzetek a papíron"
 * listát rajzolt; a 10-15-i döntés szerint viszont a jelölő a puska sora,
 * és a 10-16-i szerint a sor mellett egy jelvény mutatja a számot. Egy
 * második lista ugyanannak a sornak egy második szerkesztője volna.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { pinNumbers } from './bookingProposals/sheet';
import type { DraftRow } from './bookingProposals/types';
import type { BookingProposalsState } from './bookingProposals/useBookingProposals';
import type { PaperNote, PaperNotes, PaperPin } from './viewer/notes';

export interface PaperNotesState extends PaperNotes {
  /** A Tételsorok ide nézik, melyik jegyzet-mezőre kell fókuszálni. */
  focusRequest: { key: string } | null;
  /**
   * `Esc` a jegyzet mezőjében: vissza a papírra. A FRISSEN letett, szöveg
   * nélküli jelölő ilyenkor ELVETŐDIK — ez a „mégse".
   */
  leave: (row: DraftRow) => void;
  /**
   * A jegyzet mezőjét ELHAGYTÁK (`blur`) — a sor ettől kezdve nem friss:
   * ki van küldve, egy későbbi `Esc` már nem vetheti el.
   */
  settle: (key: string) => void;
  /**
   * A könyvelő egy TŰZETLEN sorra lépett — ha van benne jegyzet, az ő „+"-a
   * áll a papír sarkában (#68).
   */
  adopt: (key: string) => void;
  /** A Tételsorok „+"-a: a papír indítsa ennek a sornak a letételét. */
  placeLoose: (key: string) => void;
}

/** A letétel alatt álló jelölő vázlatkulcsa — csak a számolásban él. */
const GHOST = '__letetel';

export function usePaperNotes(
  booking: BookingProposalsState,
  documentId: string | null
): PaperNotesState {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [jump, setJump] = useState<{ key: string } | null>(null);
  /**
   * A FRISSEN LETETT, MÉG EL NEM HAGYOTT jelölő kulcsa. Az `Esc` CSAK ezt
   * veti el. Az `id === null` nem elég: az azonosító csak a `POST` válaszával
   * jön, és a válasz előtti `E` + `Esc` egy kimentett jelölőt törölne.
   */
  const fresh = useRef<string | null>(null);
  const [focusRequest, setFocusRequest] = useState<{ key: string } | null>(null);
  /** A tűzetlen sor, amelyiken a könyvelő utoljára állt — az ő „+"-a a sarokban. */
  const [looseKey, setLooseKey] = useState<string | null>(null);
  const [placeRequest, setPlaceRequest] = useState<{ key: string } | null>(null);
  const paper = useRef<HTMLDivElement | null>(null);
  const { rows, placePin, movePin, removeRow } = booking;

  // BIZONYLATVÁLTÁSKOR a kiemelés nem öröklődik: a következő papír kulcsai
  // mások, és egy ott maradt kulcs egy véletlenül egyező vázlatsort emelne ki.
  // biome-ignore lint/correctness/useExhaustiveDependencies: a váltás a jel, nem az érték
  useEffect(() => {
    setActiveKey(null);
    setJump(null);
    setFocusRequest(null);
    setLooseKey(null);
    setPlaceRequest(null);
    fresh.current = null;
  }, [documentId]);

  const select = useCallback((key: string | null) => {
    setActiveKey(key);
    setJump(key === null ? null : { key });
  }, []);

  /**
   * A JEGYZET MEZŐJE A TELJES KÉPERNYŐN KÍVÜL VAN — a teljes képernyő csak a
   * papír keretét nagyítja. Kilépés nélkül a fókusz egy láthatatlan mezőbe
   * kerülne, és a könyvelő vakon gépelne.
   */
  const toNoteField = useCallback((key: string) => {
    if (document.fullscreenElement) {
      void document.exitFullscreen?.().catch(() => undefined);
    }
    setFocusRequest({ key });
  }, []);

  const place = useCallback(
    (pin: PaperPin) => {
      const key = placePin(pin);
      if (key !== null) {
        fresh.current = key;
        setActiveKey(key);
        toNoteField(key);
      }
    },
    [placePin, toNoteField]
  );

  const edit = useCallback(
    (key: string) => {
      setActiveKey(key);
      toNoteField(key);
    },
    [toNoteField]
  );

  const leave = useCallback(
    (row: DraftRow) => {
      const untouched =
        fresh.current === row.key &&
        row.accountCode.trim() === '' &&
        row.amount.trim() === '' &&
        row.note.trim() === '';
      if (untouched) {
        fresh.current = null;
        removeRow(row.key);
        setActiveKey(null);
      }
      paper.current?.focus();
    },
    [removeRow]
  );

  const settle = useCallback((key: string) => {
    if (fresh.current === key) {
      fresh.current = null;
    }
  }, []);

  const numbers = booking.pinNumbers;
  // AMÍG A PUSKA TÖLT, NINCS JELÖLŐ. Bizonylatváltáskor a `rows` a betöltésig
  // az ELŐZŐ papír sorait tartja, a fájl viszont külön úton jön: ha előbb ér
  // ide, a régi jelölők az új papíron állnának, és az `1–9` azokra ugrana.
  const items: PaperNote[] = (booking.status === 'ready' ? rows : []).flatMap((row) => {
    const number = numbers.get(row.key);
    if (row.pin === null || number === undefined) {
      return [];
    }
    return [{ key: row.key, pin: row.pin, number, label: labelOf(row) }];
  });

  const numberFor = (pin: PaperPin) =>
    pinNumbers([...rows, { ...ghostRow(), pin }]).get(GHOST) ?? items.length + 1;

  const adopt = useCallback((key: string) => setLooseKey(key), []);

  const placeLoose = useCallback((key: string) => {
    setLooseKey(key);
    setPlaceRequest({ key });
  }, []);

  // ⚠️ A „+" CSAK SZÖVEGGEL JELENIK MEG, és csak TŰZETLEN soron. Az üres záró
  // sorra lépve még nincs mit a papírra tenni; a letett sor pedig már jelölő,
  // számmal. A feltétel minden renderben újra dől el, tehát a gépelés első
  // betűje hozza elő, a letétel viszi el — külön takarítás nélkül.
  const looseRow =
    booking.status === 'ready'
      ? rows.find((row) => row.key === looseKey && row.pin === null && row.note.trim() !== '')
      : undefined;
  const loose = looseRow === undefined ? null : { key: looseRow.key, label: labelOf(looseRow) };

  return {
    items,
    activeKey,
    jump,
    select,
    place,
    move: movePin,
    edit,
    numberFor,
    paper,
    focusRequest,
    leave,
    settle,
    loose,
    placeRequest,
    adopt,
    placeLoose,
  };
}

function labelOf(row: DraftRow): string {
  return row.note.trim() || row.accountCode.trim();
}

function ghostRow(): DraftRow {
  return { key: GHOST, id: null, accountCode: '', amount: '', note: '', pin: null };
}
